/**
 * Sensitivity Comparison Grid (SPRINT-25 F005)
 *
 * Displays sensitivity analysis results in a table:
 * - Base case row pinned to top (bold, with "Base Case" badge)
 * - Each non-base scenario shows adjusted metrics + delta from base
 * - Pass/fail badges based on passesServiceability
 * - Edit + Delete action buttons on non-base rows
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import type { DealSensitivityScenario } from "@shared/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SensitivityComparisonGridProps {
  scenarios: DealSensitivityScenario[];
  onEdit: (scenario: DealSensitivityScenario) => void;
  onDelete: (scenario: DealSensitivityScenario) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDecimal(value: string | null | undefined, decimals = 4): string {
  if (value == null) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return num.toFixed(decimals);
}

function formatCurrency(value: string | null | undefined): string {
  if (value == null) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDelta(value: string | null | undefined, formatter: (v: string) => string): string {
  if (value == null) return "";
  const num = parseFloat(value);
  if (isNaN(num) || num === 0) return "";
  const sign = num > 0 ? "+" : "";
  return `(${sign}${formatter(value)})`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SensitivityComparisonGrid({
  scenarios,
  onEdit,
  onDelete,
}: SensitivityComparisonGridProps) {
  // Sort: base case first, then by displayOrder
  const sorted = [...scenarios].sort((a, b) => {
    if (a.isBaseCase && !b.isBaseCase) return -1;
    if (!a.isBaseCase && b.isBaseCase) return 1;
    return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
  });

  const baseCase = sorted.find((s) => s.isBaseCase);

  return (
    <div data-testid="sensitivity-comparison-grid">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Scenario Name</TableHead>
            <TableHead className="text-right">Rate Change (bps)</TableHead>
            <TableHead className="text-right">Income Change (%)</TableHead>
            <TableHead className="text-right">DSCR</TableHead>
            <TableHead className="text-right">Surplus</TableHead>
            <TableHead className="text-center">Pass/Fail</TableHead>
            <TableHead className="text-right w-20">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((scenario) => {
            const isBase = scenario.isBaseCase;

            // Compute delta vs base case for display
            let dscrDelta: string | null = null;
            let surplusDelta: string | null = null;

            if (!isBase && baseCase) {
              if (scenario.adjustedDscr != null && baseCase.adjustedDscr != null) {
                const delta = parseFloat(scenario.adjustedDscr) - parseFloat(baseCase.adjustedDscr);
                dscrDelta = delta.toFixed(4);
              }
              if (scenario.adjustedSurplus != null && baseCase.adjustedSurplus != null) {
                const delta = parseFloat(scenario.adjustedSurplus) - parseFloat(baseCase.adjustedSurplus);
                surplusDelta = delta.toFixed(2);
              }
            }

            const rowTestId = isBase ? "row-scenario-base" : `row-scenario-${scenario.id}`;

            return (
              <TableRow
                key={scenario.id}
                data-testid={rowTestId}
                className={isBase ? "font-semibold bg-muted/30" : undefined}
              >
                {/* Scenario Name */}
                <TableCell className="py-2">
                  <div className="flex items-center gap-2">
                    <span>{scenario.scenarioName}</span>
                    {isBase && (
                      <Badge
                        variant="outline"
                        className="text-xs"
                        data-testid="badge-base-case"
                      >
                        Base Case
                      </Badge>
                    )}
                  </div>
                </TableCell>

                {/* Rate Change */}
                <TableCell className="text-right py-2">
                  {scenario.rateChangeBps != null ? `${scenario.rateChangeBps}bps` : "-"}
                </TableCell>

                {/* Income Change */}
                <TableCell className="text-right py-2">
                  {scenario.incomeChangePercent != null
                    ? `${parseFloat(scenario.incomeChangePercent as string).toFixed(1)}%`
                    : "-"}
                </TableCell>

                {/* DSCR */}
                <TableCell className="text-right py-2">
                  <div className="flex flex-col items-end">
                    <span>{formatDecimal(scenario.adjustedDscr as string | null)}</span>
                    {!isBase && dscrDelta && (
                      <span className="text-xs text-muted-foreground">
                        {formatDelta(dscrDelta, (v) => parseFloat(v).toFixed(4))}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Surplus */}
                <TableCell className="text-right py-2">
                  <div className="flex flex-col items-end">
                    <span
                      className={
                        scenario.adjustedSurplus != null && parseFloat(scenario.adjustedSurplus as string) < 0
                          ? "text-destructive"
                          : undefined
                      }
                    >
                      {formatCurrency(scenario.adjustedSurplus as string | null)}
                    </span>
                    {!isBase && surplusDelta && (
                      <span className="text-xs text-muted-foreground">
                        {formatDelta(surplusDelta, (v) => formatCurrency(v))}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Pass/Fail Badge */}
                <TableCell className="text-center py-2">
                  {scenario.passesServiceability === true && (
                    <Badge
                      className="bg-green-100 text-green-800 hover:bg-green-100"
                      data-testid={isBase ? "badge-pass-base" : `badge-pass-${scenario.id}`}
                    >
                      Pass
                    </Badge>
                  )}
                  {scenario.passesServiceability === false && (
                    <Badge
                      variant="destructive"
                      data-testid={isBase ? "badge-fail-base" : `badge-fail-${scenario.id}`}
                    >
                      Fail
                    </Badge>
                  )}
                  {scenario.passesServiceability == null && (
                    <span className="text-xs text-muted-foreground">-</span>
                  )}
                </TableCell>

                {/* Actions */}
                <TableCell className="text-right py-2">
                  {!isBase && (
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(scenario)}
                        data-testid={`button-edit-scenario-${scenario.id}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDelete(scenario)}
                        data-testid={`button-delete-scenario-${scenario.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
